import React, { useMemo } from "react";
import type { FC, MouseEventHandler } from "react";

import cn from "classnames";

import { ButtonCore } from "../../helpers/common/ButtonCore";
import { ButtonDiv } from "../../helpers/common/ButtonDiv";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { extractProps } from "../../utils/react";

import type { BannerProps } from "./types";

import classes from "./Banner.styl";

const BANNER_TEST_ID = "banner-test-id";
const BANNER_DISPLAY_NAME = "Banner";

/**
 * Компонент, умеющий наделять контейнер такими свойствами, как цвет фона, цвет и толщина обводки, тень.
 * Умеет обрабатывать состояния как внутренних элементов, так и самого себя. Управляет внутренними отступами.
 *
 * <a href="https://www.figma.com/design/SZCPsHnuqBIWlYSyfmq0j1/🧩-Sheets-%26-Modules?node-id=608-87585&t=6z1Nn6Egl8ZFahBk-1" target="_blank">Спека</a>
 * @constructor
 */
export const Banner: FC<BannerProps> = (props) => {
    const { customColorsStyles, currentProps } = useMemo(
        () => extractProps(props, ["customColorsStyles"]),
        [props],
    );

    const {
        state = "none",
        view = "secondary",
        border = false,
        customVarsStyles,
        children,
        onClick,
        onMouseDown,
        width = "100%",
        "data-tsid": tsid,
        ...otherProps
    } = currentProps;

    const { clicked, mousedownHandler } = useMouseDownAnimation();

    const mouseDownHandler: MouseEventHandler<HTMLDivElement> = (event) => {
        onMouseDown?.(event);
        mousedownHandler();
    };

    const containerClasses = cn(
        classes["container"],
        classes[`__view-${view}`],
        {
            [classes["__clicked"]]: clicked,
            [classes["__state-loading"]]: state === "loading",
            [classes["__state-enabled"]]: state === "enabled",
            [classes["__border"]]: border,
        },
        customColorsStyles,
        customVarsStyles,
    );

    const dataTestId = tsid || BANNER_TEST_ID;

    if (state === "none") {
        return (
            <div
                className={containerClasses}
                style={{ width }}
                data-tsid={dataTestId}
                data-uikit={BANNER_DISPLAY_NAME}
            >
                {children}
            </div>
        );
    }

    return (
        <ButtonCore
            as={ButtonDiv}
            {...otherProps}
            style={{ width }}
            wrapperClassName={containerClasses}
            onClick={onClick}
            onMouseDown={mouseDownHandler}
            state={state}
            data-tsid={dataTestId}
            data-uikit={BANNER_DISPLAY_NAME}
        >
            <div className={classes["content"]}>{children}</div>
        </ButtonCore>
    );
};

Banner.displayName = BANNER_DISPLAY_NAME;

export default Banner;
